/**
 * Public pricing page — /pricing
 * Geo-aware, Steel & Ember branded. Works pre-login.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, Gauge, Zap, ShieldCheck, Info } from 'lucide-react';
import { billingApi } from '../services/billing';
import { detectCountry, currencyForCountry, formatMoney, COUNTRY_NAMES, COUNTRY_FLAGS } from '../lib/geo';
import { CognaraLogo } from '../components/instrument/CognaraLogo';
import { CognaraWordmark } from '../components/instrument/CognaraWordmark';

const FAQS = [
  { q: 'What is a credit?', a: '1 credit = 1,000 AI tokens. A typical query uses ~2 credits on Cognara Engine · Pro 4.6 or ~3 credits on Cognara Engine · Apex 4.8.' },
  { q: 'Do credits expire?', a: 'No. Credits never expire. Your balance carries forward indefinitely.' },
  { q: 'Can I switch models?', a: 'Yes — choose the engine per query. More powerful engines consume more credits per query (Pro 4.6: 1.0×, Ultra 4.7: 1.2×, Apex 4.8: 1.5×).' },
  { q: 'What happens when I run out?', a: 'You will see a prompt to purchase more credits. You can also enable Auto Top-up so the wallet refills automatically.' },
  { q: 'Do I need a subscription?', a: 'No. Cognara is fully pay-as-you-go. Buy credits when you need them.' },
  { q: 'How does volume pricing work?', a: 'The more credits you buy over your lifetime, the lower your per-credit rate. Your rate never resets upward.' },
];

// Maps internal model id → user-facing tier label. Single source of truth
// so we never expose "Claude" / "Opus" to end users.
const MODEL_LABEL = {
  'claude-opus-4-6': 'Pro 4.6',
  'claude-opus-4-7': 'Ultra 4.7',
  'claude-opus-4-8': 'Apex 4.8',
};
const prettyModel = (id) => MODEL_LABEL[id] || (id || '').replace('claude-', '').replace(/-/g, ' ');

const Pricing = () => {
  const [country, setCountry] = useState('US');
  const [data, setData] = useState(null);
  const [customQty, setCustomQty] = useState(500);
  const [customQuote, setCustomQuote] = useState(null);
  const [selectedModel, setSelectedModel] = useState('claude-opus-4-7');
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    (async () => {
      const cc = await detectCountry();
      setCountry(cc);
      const { data } = await billingApi.publicPricing(cc);
      setData(data);
    })();
  }, []);

  useEffect(() => {
    if (!data) return;
    let alive = true;
    // Pass `selectedModel` so the custom quote uses that model's bracket rates.
    billingApi.publicQuote(Math.max(1, Number(customQty) || 1), data.currency, selectedModel)
      .then((r) => alive && setCustomQuote(r.data))
      .catch(() => {});
    return () => { alive = false; };
  }, [customQty, data, selectedModel]);

  const currency = data?.currency || 'USD';
  const flag = COUNTRY_FLAGS[country] || '🌐';
  const countryName = COUNTRY_NAMES[country] || country;

  // Per-query cost preview — base query is 2.0 credits; final deduction
  // scales with the model multiplier (1.0× / 1.2× / 1.5×). Cost uses the
  // 0–1,000 bracket rate as a "from $..." chip.
  const modelCostPerQuery = useMemo(() => {
    if (!data) return [];
    const ratesByModel = data.rates_by_model || {};
    const labelFor = (m) => (
      m === 'claude-opus-4-6' ? 'Cognara Engine · Pro 4.6 (balanced)' :
      m === 'claude-opus-4-7' ? 'Cognara Engine · Ultra 4.7 (premium)' :
      m === 'claude-opus-4-8' ? 'Cognara Engine · Apex 4.8 (max reasoning)' :
      `Cognara Engine · ${prettyModel(m)}`
    );
    return Object.entries(data.multipliers).map(([m, mult]) => {
      const rate = (ratesByModel[m] || {})['0-1000'] || 0;
      const base = 2.0;
      const deducted = base * (mult || 1);
      return { model: m, base, deducted, cost: deducted * rate, label: labelFor(m), rate };
    });
  }, [data]);

  // Bracket-sliced cost helper: replicates backend credits.price_for() at
  // lifetime=0 so we can show what 1k/5k/10k credits cost on each engine
  // without firing 9 parallel /quote requests. Uses data.rates_by_model.
  const priceFor = (qty, model) => {
    const rates = (data?.rates_by_model || {})[model];
    if (!rates) return null;
    const ranges = [
      { from: 0,     to: 1000,   key: '0-1000' },
      { from: 1000,  to: 5000,   key: '1001-5000' },
      { from: 5000,  to: 10000,  key: '5001-10000' },
      { from: 10000, to: Infinity, key: '10001+' },
    ];
    let remaining = qty, total = 0;
    for (const r of ranges) {
      if (remaining <= 0) break;
      const cap = r.to - r.from;
      const take = Math.min(remaining, cap);
      total += take * (rates[r.key] || 0);
      remaining -= take;
    }
    return total;
  };

  const comparisonRows = useMemo(() => {
    if (!data?.rates_by_model) return [];
    const QUANTITIES = [1000, 5000, 10000];
    const MODELS = ['claude-opus-4-6', 'claude-opus-4-7', 'claude-opus-4-8'];
    return QUANTITIES.map((qty) => ({
      qty,
      cells: MODELS.map((m) => ({ model: m, cost: priceFor(qty, m) })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="min-h-screen" style={{ background: '#FAFBFC' }} data-testid="pricing-page">
      {/* Header */}
      <header className="border-b" style={{ borderColor: '#E2E8F0' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-home">
            <CognaraLogo variant="icon" size={30} style={{ color: '#0A1B2E' }} />
            <CognaraWordmark size={20} color="#0A1B2E" />
          </Link>
          <div className="flex items-center gap-4 text-[13px]">
            <Link to="/portal/login" className="text-nexus-ink-dim hover:text-nexus-ink-primary transition-colors" style={{ color: '#4A5568' }}>Sign in</Link>
            <Link to="/portal/register"
              className="px-4 py-2 rounded-lg text-white text-[13px] font-medium hover:opacity-90 transition-opacity"
              style={{ background: '#0A1B2E' }}
              data-testid="get-started-btn">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] mb-6"
          style={{ background: 'rgba(242,107,31,0.08)', color: '#F26B1F', border: '1px solid rgba(242,107,31,0.2)' }}>
          <span className="font-semibold tracking-wider">PRICING IN USD · GLOBAL</span>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight"
          style={{ color: '#0A1B2E' }}>
          Simple, transparent pricing
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="mt-4 text-[17px] max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
          Pay only for what you use. No subscriptions. No commitments. <br className="hidden sm:block" />
          Every new user gets <span className="font-semibold" style={{ color: '#0A1B2E' }}>{data?.free_signup_credits || 20} free credits</span> to try Cognara.
        </motion.p>
      </section>

      {/* Packs */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(data?.packs || []).map((p, i) => {
            const isRecommended = p.recommended;
            const isFree = !p.paid;
            return (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                className="relative rounded-2xl p-6 flex flex-col"
                style={{
                  background: isRecommended ? '#0A1B2E' : '#fff',
                  border: isRecommended ? '1px solid #0A1B2E' : '1px solid #E2E8F0',
                  color: isRecommended ? '#fff' : '#1A202C',
                  boxShadow: isRecommended ? '0 20px 40px -10px rgba(10,27,46,0.3)' : '0 4px 12px -4px rgba(0,0,0,0.06)',
                }}
                data-testid={`pack-${p.id}`}>
                {isRecommended && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider"
                    style={{ background: '#F26B1F', color: '#fff' }}>RECOMMENDED</span>
                )}
                <div className="text-[12px] font-semibold tracking-wider uppercase" style={{ color: isRecommended ? '#F26B1F' : '#F26B1F' }}>
                  {p.name}
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading text-4xl font-semibold">{p.credits.toLocaleString()}</span>
                  <span className="text-[13px]" style={{ color: isRecommended ? '#A8B0C0' : '#718096' }}>credits</span>
                </div>
                <div className="mt-2 font-heading text-2xl font-semibold">
                  {isFree ? 'Free' : formatMoney(p.price.total, currency)}
                </div>
                <p className="mt-3 text-[13px]" style={{ color: isRecommended ? '#A8B0C0' : '#4A5568' }}>{p.desc}</p>
                <div className="mt-4 text-[11px] opacity-80" style={{ color: isRecommended ? '#A8B0C0' : '#718096' }}>
                  Primary model: <span className="font-medium">{prettyModel(p.primary_model)}</span>
                </div>
                <Link to={isFree ? '/portal/register' : `/portal/pricing?credits=${p.credits}&pack=${p.id}`}
                  className="mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-all hover:opacity-90"
                  style={{
                    background: isRecommended ? '#F26B1F' : (isFree ? '#F7FAFC' : '#0A1B2E'),
                    color: isRecommended ? '#fff' : (isFree ? '#0A1B2E' : '#fff'),
                  }}
                  data-testid={`pack-cta-${p.id}`}>
                  {isFree ? 'Get started free' : 'Buy now'} <ArrowRight size={14} />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Custom quantity */}
        <div className="mt-10 rounded-2xl p-6 md:p-8"
          style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
          <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>BUILD YOUR OWN</div>
              <h3 className="font-heading text-2xl font-semibold mt-1" style={{ color: '#0A1B2E' }}>Or buy any amount</h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-4 items-end">
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#718096' }}>Credits</div>
              <input type="number" min="1" value={customQty} onChange={(e) => setCustomQty(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-[16px] font-medium outline-none focus:ring-2"
                style={{ background: '#F7FAFC', border: '1px solid #E2E8F0', color: '#0A1B2E' }}
                data-testid="custom-credits-input" />
            </label>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#718096' }}>Estimate for model</div>
              <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-[14px] font-medium outline-none focus:ring-2"
                style={{ background: '#F7FAFC', border: '1px solid #E2E8F0', color: '#0A1B2E' }}
                data-testid="custom-model-select">
                <option value="claude-opus-4-6">Cognara Engine · Pro 4.6 (1.0× balanced)</option>
                <option value="claude-opus-4-7">Cognara Engine · Ultra 4.7 (1.2× premium)</option>
                <option value="claude-opus-4-8">Cognara Engine · Apex 4.8 (1.5× max reasoning)</option>
              </select>
            </label>
            <div className="md:text-right">
              <div className="text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#718096' }}>Total</div>
              <div className="font-heading text-3xl font-semibold" style={{ color: '#0A1B2E' }}>
                {customQuote ? formatMoney(customQuote.total, currency) : '—'}
              </div>
            </div>
          </div>
          {customQuote && Math.round(Number(customQty)) * (data?.multipliers[selectedModel] || 1) > 0 && (
            <div className="mt-3 text-[12px]" style={{ color: '#4A5568' }}>
              That&apos;s approximately <b>{Math.floor(Number(customQty) / (2 * (data?.multipliers[selectedModel] || 1))).toLocaleString()} queries</b> on {prettyModel(selectedModel)}.
            </div>
          )}
          <Link to={`/portal/pricing?credits=${customQty}`}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: '#F26B1F' }} data-testid="custom-buy-btn">
            Buy {customQty} credits <ArrowRight size={14} />
          </Link>
        </div>

        {/* Per-model bracket comparison — what 1k/5k/10k credits cost on each engine */}
        <div className="mt-6 rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #E2E8F0' }} data-testid="model-comparison-card">
          <div className="px-6 md:px-8 pt-6 pb-2 flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>SIZE YOUR WALLET</div>
              <h3 className="font-heading text-xl font-semibold mt-1" style={{ color: '#0A1B2E' }}>Per-engine bracket comparison</h3>
              <p className="mt-1 text-[12px]" style={{ color: '#718096' }}>
                What a fresh purchase costs across engines at lifetime = 0 (volume-discounted).
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="model-comparison-table">
              <thead>
                <tr style={{ background: '#F7FAFC', borderTop: '1px solid #E2E8F0' }}>
                  <th className="text-left px-6 md:px-8 py-3 font-semibold text-[11px] uppercase tracking-wider" style={{ color: '#718096', width: '24%' }}>Credits</th>
                  <th className="text-right px-4 py-3 font-semibold text-[11px] uppercase tracking-wider" style={{ color: '#718096' }}>
                    <div>Pro 4.6</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: '#A0AEC0' }}>1.0× · balanced</div>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold text-[11px] uppercase tracking-wider" style={{ color: '#718096' }}>
                    <div>Ultra 4.7</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: '#A0AEC0' }}>1.2× · premium</div>
                  </th>
                  <th className="text-right px-6 md:px-8 py-3 font-semibold text-[11px] uppercase tracking-wider" style={{ color: '#718096' }}>
                    <div>Apex 4.8</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{ color: '#A0AEC0' }}>1.5× · max reasoning</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.qty} className="border-t" style={{ borderColor: '#F2F5F8' }} data-testid={`comparison-row-${row.qty}`}>
                    <td className="px-6 md:px-8 py-4">
                      <div className="font-heading text-lg font-semibold" style={{ color: '#0A1B2E' }}>
                        {row.qty.toLocaleString()}
                      </div>
                      <div className="text-[11px]" style={{ color: '#718096' }}>credits</div>
                    </td>
                    {row.cells.map((c, idx) => (
                      <td key={c.model}
                        className={`py-4 text-right font-mono ${idx === row.cells.length - 1 ? 'px-6 md:px-8' : 'px-4'}`}
                        data-testid={`cell-${row.qty}-${c.model}`}>
                        <div className="font-semibold text-[15px]" style={{ color: '#0A1B2E' }}>
                          {c.cost == null ? '—' : formatMoney(c.cost, currency)}
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: '#A0AEC0' }}>
                          {c.cost == null ? '' : `${formatMoney(c.cost / row.qty, currency)}/cr`}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 md:px-8 py-3 text-[11px] flex items-start gap-1.5 border-t" style={{ background: '#FAFBFC', borderColor: '#E2E8F0', color: '#718096' }}>
            <Info size={11} className="mt-0.5 flex-shrink-0" />
            <span>
              Volume brackets stack automatically — buying 5,000 credits pays the 0–1,000 rate on the first thousand, then the 1,001–5,000 rate on the rest. Your lifetime spend never resets, so each purchase pushes you further down the curve.
            </span>
          </div>
        </div>

        {/* Model multiplier explainer */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl p-6" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
            <div className="text-[11px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>HOW CREDITS WORK</div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b" style={{ borderColor: '#E2E8F0' }}>
                  <th className="text-left pb-2 font-semibold" style={{ color: '#0A1B2E' }}>Model</th>
                  <th className="text-right pb-2 font-semibold" style={{ color: '#0A1B2E' }}>Per query</th>
                  <th className="text-right pb-2 font-semibold" style={{ color: '#0A1B2E' }}>Your cost</th>
                </tr>
              </thead>
              <tbody>
                {modelCostPerQuery.map((row) => (
                  <tr key={row.model} className="border-b" style={{ borderColor: '#F2F5F8' }}>
                    <td className="py-2.5" style={{ color: '#4A5568' }}>{row.label}</td>
                    <td className="py-2.5 text-right font-mono" style={{ color: '#4A5568' }}>{row.deducted.toFixed(1)} cr</td>
                    <td className="py-2.5 text-right font-semibold" style={{ color: '#0A1B2E' }}>{formatMoney(row.cost, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-[11px] flex items-start gap-1.5" style={{ color: '#718096' }}>
              <Info size={11} className="mt-0.5 flex-shrink-0" />
              <span>Based on a typical 2,000-token query at your 0–1,000 bracket rate.</span>
            </div>
          </div>

          <div className="rounded-2xl p-6" style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
            <div className="text-[11px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>BUY MORE, PAY LESS</div>
            <div className="space-y-2">
              {(data?.brackets || []).map((b, i) => {
                const rate = data.rates[b.label];
                const base = data.rates['0-1000'];
                const savings = i === 0 ? 0 : Math.round((1 - rate / base) * 100);
                return (
                  <div key={b.label} className="flex items-center justify-between py-2.5 border-b" style={{ borderColor: '#F2F5F8' }}>
                    <div className="text-[13px]" style={{ color: '#4A5568' }}>
                      {b.max ? `${b.min.toLocaleString()} – ${b.max.toLocaleString()} credits` : `${b.min.toLocaleString()}+ credits`}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold" style={{ color: '#0A1B2E' }}>{formatMoney(rate, currency)}</span>
                      <span className="text-[11px]" style={{ color: '#718096' }}>/ credit</span>
                      {savings > 0 && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ background: 'rgba(110,231,183,0.15)', color: '#10B981' }}>
                          −{savings}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-[11px]" style={{ color: '#718096' }}>
              Bracket is based on your lifetime credits purchased. It never resets upward.
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12">
          <h3 className="font-heading text-2xl font-semibold mb-6" style={{ color: '#0A1B2E' }}>Frequently asked questions</h3>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={i} className="rounded-xl overflow-hidden"
                style={{ background: '#fff', border: '1px solid #E2E8F0' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid={`faq-${i}`}>
                  <span className="font-medium text-[14px]" style={{ color: '#0A1B2E' }}>{f.q}</span>
                  <span className="text-[18px]" style={{ color: '#F26B1F' }}>{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-[13px] leading-relaxed" style={{ color: '#4A5568' }}>{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t py-6" style={{ borderColor: '#E2E8F0', background: '#fff' }}>
        <div className="max-w-6xl mx-auto px-6 text-center text-[11px]" style={{ color: '#718096' }}>
          © {new Date().getFullYear()} Capexara AI (OPC) Private Limited · Cognara Expert Engine
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
